import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenu} from '@Components/contextual-menu';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import {getCurrentPage, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {openCropperModal} from '@Modals/cropper-modal/cropper-modal.library';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import {openReplaceBackgroundImageModal} from '@Modals/replace-background-image-modal';
import ImageBackgroundFiltersOptions from '@Panels/poster-editor-image-background-options/components/image-background-filters-options';
import ImageBackgroundAdjustmentOptions from '@Panels/poster-editor-image-background-options/components/image-background-adjustment-options';
import {ImageBackground} from '@PosterWhiteboard/page/background/image-background.class';
import {useAppSelector} from '@/hooks';

interface PosterEditorImageBackgroundOptionsProps {
  panelId: string;

  onBack(): void;

  onClose(): void;
}

enum BackgroundImageOption {
  CROP = 'CROP',
  REPLACE = 'REPLACE',
  FILTERS = 'FILTERS',
  ADJUSMENTS = 'ADJUSMENTS',
}

enum PanelsToShow {
  FILTERS = 'FILTERS',
  ADJUSMENTS = 'ADJUSMENTS',
  DEFAULT = 'DEFAULT',
}

export default function PosterEditorImageBackgroundOptions({...props}: PosterEditorImageBackgroundOptionsProps): ReactElement | null {
  const [currentlyActivePanel, setCurrentlyActivePanel] = useState(PanelsToShow.DEFAULT);
  const currentPageObject = useAppSelector(getCurrentPage);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  useEffect((): void => {
    updateHeightForModal();
  }, [currentlyActivePanel]);

  const updateHeightForModal = (): void => {
    if (currentlyActivePanel !== PanelsToShow.DEFAULT) {
      return;
    }
    updateOptionsPanelHeight(props.panelId, `158px`);
  };

  if (!currentBackgroundObject || !currentPageObject) {
    return null;
  }

  if (currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onBackButton={(): void => {
          if (currentlyActivePanel === PanelsToShow.DEFAULT) {
            props.onBack();
          } else {
            setCurrentlyActivePanel(PanelsToShow.DEFAULT);
          }
        }}
        showBackButton
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_background_image')}
      />
    );
  };

  const getItems = (): Array<BottomBarItemProps> => {
    return [
      {
        id: BackgroundImageOption.CROP,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
      },
      {
        id: BackgroundImageOption.REPLACE,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
      },
      {
        id: BackgroundImageOption.FILTERS,
        icon: 'icon-filters',
        text: window.i18next.t('pmwjs_filters'),
      },
      {
        id: BackgroundImageOption.ADJUSMENTS,
        icon: 'icon-adjustments',
        text: window.i18next.t('pmwjs_adjustments'),
      },
    ];
  };

  const onCropCallback = (response: CropperPanelApplyResponse): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }
    void currentPage.background.updateFromObject({
      details: {
        imageBackgroundItemObject: {
          cropData: {
            cropped: true,
            ...response.cropData,
          },
        },
      },
    });
  };

  const onClickCropBg = (): void => {
    const poster = window.posterEditor?.whiteboard?.pages.poster;
    if (!poster) {
      return;
    }

    const backgroundItem = poster.getCurrentPage().background.details;
    if (!(backgroundItem instanceof ImageBackground)) {
      return;
    }

    openCropperModal({
      onApply: onCropCallback,
      imageHashedFilename: backgroundItem.imageBackgroundItem.hashedFilename,
      imageSource: backgroundItem.imageBackgroundItem.imageSource,
      dataUrl: backgroundItem.imageBackgroundItem.uploadingImageData?.dataUrl,
      showRemoveBackgroundOption: false,
      initialCropData: backgroundItem.imageBackgroundItem.cropData,
      showCroppingPresets: false,
      aspectRatio: poster.width / poster.height,
    });
  };

  const onClickHandler = (id: BackgroundImageOption): void => {
    switch (id) {
      case BackgroundImageOption.FILTERS:
        setCurrentlyActivePanel(PanelsToShow.FILTERS);
        break;
      case BackgroundImageOption.CROP:
        onClickCropBg();
        return;
      case BackgroundImageOption.ADJUSMENTS:
        setCurrentlyActivePanel(PanelsToShow.ADJUSMENTS);
        break;
      case BackgroundImageOption.REPLACE:
        openReplaceBackgroundImageModal();
        break;
      default:
        break;
    }
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu contentClassName="content-body" id="image-background-options" hasMobileModalPadding hasMobileMorphingProperties>
        <BottomBar isForEditorBottomBar={false} id="image-bg-bottom-bar" items={getItems()} onClick={onClickHandler} />
      </ContextualMenu>
    );
  };

  const onBack = (): void => {
    setCurrentlyActivePanel(PanelsToShow.DEFAULT);
  };

  const getPanels = (): ReactElement | null => {
    switch (currentlyActivePanel) {
      case PanelsToShow.DEFAULT:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
      case PanelsToShow.ADJUSMENTS:
        return (
          <ImageBackgroundAdjustmentOptions
            onClose={(): void => {
              props.onClose();
            }}
            panelId={props.panelId}
            onBack={onBack}
          />
        );
      case PanelsToShow.FILTERS:
        return (
          <ImageBackgroundFiltersOptions
            onClose={(): void => {
              props.onClose();
            }}
            onBack={onBack}
            panelId={props.panelId}
          />
        );
      default:
        return null;
    }
  };

  return getPanels();
}
